import * as React from "react";
import { request } from "../../../services/cpln";
import { SyntaxHighlight } from "../../../components/syntaxHighlight/SyntaxHighlight";
import { Alert, Button, notification } from "antd";
import { defaultValues, toSelectOptions } from "../../../services/defaults";
import { ExternalLink } from "../../../newcomponents/table/components/ExternalLink";
import { v4 as uuidv4 } from "uuid";
import { useCpFormValue } from "../../gvc/testNewForm/utils";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { CpSelect } from "../../gvc/testNewForm/CpSelect";
import { CpDescription } from "../../gvc/testNewForm/CpDescription";
import { CpInput } from "../../gvc/testNewForm/CpInput";
import { CpArrayObject } from "../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../workload/draft/CpArrayItemNameValuePair";
import { CpErrorWarning } from "../../gvc/testNewForm/CpErrorWarning";
import { CpKindSelect } from "../../gvc/testNewForm/temp/CpKindSelect";

interface Props {
  index: number;
}
export const CloudWatch: React.FC<Props> = ({ index }) => {
  const [regionOptions, setRegionOptions] = React.useState<{ label: string; value: string }[]>([]);
  async function fetchRegionOptions() {
    const { data } = await request({ url: "/catalog/schema/org" });
    const options = data.keys.spec.keys.logging.keys.cloudWatch.keys.region.allow;
    setRegionOptions(toSelectOptions(options.sort()));
  }
  React.useEffect(() => {
    fetchRegionOptions();
  }, []);
  const regionOptionsToUse =
    regionOptions.length > 0 ? regionOptions : toSelectOptions(defaultValues.org.logging.cloudWatch.region.options);

  const region = useCpFormValue(`logging.${index}.cloudWatch.region`);

  const policy = `{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Action": [
      "logs:CreateLogStream",
      "logs:CreateLogGroup",
      "logs:PutLogEvents",
      "logs:PutRetentionPolicy"
    ],
    "Resource": "*"
  }]
}`;

  return (
    <>
      <div style={{ maxWidth: 1000 }}>
        <CpFormItem>
          <CpLabel name={`logging.${index}.cloudWatch.region`}>Region</CpLabel>
          <CpSelect
            options={regionOptionsToUse}
            name={`logging.${index}.cloudWatch.region`}
            style={{ maxWidth: 600 }}
          />
          <CpErrorWarning name={`logging.${index}.cloudWatch.region`} />
          {region ? (
            <div className="-mt-2 mb-4">
              <ExternalLink
                label="Explore CloudWatch"
                url={`https://${region}.console.aws.amazon.com/cloudwatch/home?#logsV2:log-groups`}
                disableCopy
              />
            </div>
          ) : null}
        </CpFormItem>
        <CpFormItem>
          <CpLabel name={`logging.${index}.cloudWatch.credentials`}>Credentials</CpLabel>
          <CpDescription>Select an AWS secret to access CloudWatch</CpDescription>
          <CpKindSelect
            kind="secret"
            queries={[{ property: "type", value: "aws" }]}
            name={`logging.${index}.cloudWatch.credentials`}
            optionValue="name"
            style={{ maxWidth: 600 }}
          />
          <CpErrorWarning name={`logging.${index}.cloudWatch.credentials`} />
        </CpFormItem>
        <div className="mb-12 mt-2">
          <Alert className="mb-2" message="Policy attached to the user in IAM must at least look like this:" />
          <Button
            className="mb-2"
            type={"default"}
            onClick={() => {
              navigator.clipboard.writeText(policy);
              notification.success({ message: `Copied policy to clipboard` });
            }}
          >
            Copy Policy
          </Button>
          <SyntaxHighlight
            code={policy}
            language="json"
          />
        </div>
        <CpFormItem>
          <CpLabel name={`logging.${index}.cloudWatch.groupName`}>Group Name</CpLabel>
          <CpDescription>
            <div>
              <div>
                Use $stream, $location, $provider, $replica, $workload, $gvc, $org, $container, $version to template.
              </div>
              <div>Any metadata can be used that’s associated with the workload.</div>
              <div>
                Metadata can be associated as a comma separated key-value pair under the tag “cpln/logMetadata” on the
                workloads.
              </div>
              <div>Example Tag; cpln/logMetadata=”metadataKeyA=metadataValueA,metadataKeyB=metadataValueB”</div>
            </div>
          </CpDescription>
          <CpInput name={`logging.${index}.cloudWatch.groupName`} />
          <CpErrorWarning name={`logging.${index}.cloudWatch.groupName`} />
        </CpFormItem>
        <CpFormItem>
          <CpLabel name={`logging.${index}.cloudWatch.streamName`}>Stream Name</CpLabel>
          <CpDescription>
            <div>
              <div>
                Use $stream, $location, $provider, $replica, $workload, $gvc, $org, $container, $version to template.
              </div>
              <div>Any metadata can be used that’s associated with the workload.</div>
              <div>
                Metadata can be associated as a comma separated key-value pair under the tag “cpln/logMetadata” on the
                workloads.
              </div>
              <div>Example Tag; cpln/logMetadata=”metadataKeyA=metadataValueA,metadataKeyB=metadataValueB”</div>
            </div>
          </CpDescription>
          <CpInput name={`logging.${index}.cloudWatch.streamName`} />
          <CpErrorWarning name={`logging.${index}.cloudWatch.streamName`} />
        </CpFormItem>
        <CpFormItem>
          <CpLabel name={`logging.${index}.cloudWatch.retentionDays`}>Retention Days</CpLabel>
          <CpSelect
            name={`logging.${index}.cloudWatch.retentionDays`}
            options={getRetentionDayOptions()}
            style={{ maxWidth: 180 }}
          />
          <CpErrorWarning name={`logging.${index}.cloudWatch.retentionDays`} />
        </CpFormItem>
        <CpArrayObject
          name={`logging.${index}.cloudWatch.extractFields`}
          label="Extract Fields"
          labels={["Key", "Value"]}
          factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
        >
          <CpArrayItemNameValuePair />
        </CpArrayObject>
      </div>
    </>
  );
};

function getRetentionDayOptions() {
  const values = [1, 3, 5, 7, 14, 30, 60, 90, 120, 150, 180, 365, 400, 545, 731, 1827, 3653];
  return toSelectOptions(values.map((v) => String(v)));
}
